import { Suspense, useEffect, useState } from 'react';
import Sidebar from './Sidebar';
import Header from './Header';
import Footer from './Footer';

// 🏛️ The Master Shell Snippet
import { AppShell } from '../components/snippets/app-shell.snippet.jsx';
import { LoadingState } from '../components/blocks/loading-state.block.jsx';
// Stacked auth-side banners (admin message + email verification).
// Rendered above the page content so they're visible on every screen
// without requiring per-page wiring.
import { AccountBanners, VerificationGate } from '../components/blocks/account-banners.block.jsx';
import { RefusalNotice } from '../components/blocks/refusal-notice.block.jsx';
import { useAuthContext } from '../context/AuthContext';
import useIdleSession, { DEFAULT_IDLE_MS, DEFAULT_WARN_MS } from '../hooks/useIdleSession';
import useActiveMinutes from '../hooks/useActiveMinutes';
import { useSidebarCollapsed } from '../hooks/useSidebarCollapsed.js';
import {
  SIGN_OUT_REASONS,
  currentPath,
  leaveAfterSignOutElsewhere,
  signOutAndLeave
} from '../modules/auth/sessionSignals';

import { Text } from '../components/elements/text.element.jsx';
import { Button } from '../components/elements/Button.jsx';
export default function Layout({ children }) {
  // Every active minute on Moksy earns (earning plan time_active).
  useActiveMinutes();
  const { isAuthenticated, logout } = useAuthContext();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // Held in a hook rather than here, because the journey chrome mounts the
  // same sidebar and a second copy of this logic is how the two surfaces start
  // disagreeing about one preference.
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useSidebarCollapsed();

  // Idle sessions end HERE, in the authenticated shell, rather than in the API
  // client. The client is reactive by design — it can only notice a dead
  // session when a request is made — so an idle tab kept offering actions the
  // session could no longer perform, and the person found out mid-click.
  //
  // The idle window is read from the runtime config so an operator can set it,
  // with a default rather than a hard requirement: a missing setting must not
  // mean "never expire".
  const idleMs = Number(import.meta.env.VITE_SESSION_IDLE_MS) || DEFAULT_IDLE_MS;
  const { warning, secondsLeft, staySignedIn } = useIdleSession({
    enabled: isAuthenticated,
    idleMs,
    warnMs: DEFAULT_WARN_MS,
    onExpire: () => {
      // The login page, with the reason and the page they were on — the same
      // shape api/client.js uses for a 401. This used to go to
      // `/?reason=session_expired`, where nothing reads the reason, and the
      // page they were working on was lost.
      signOutAndLeave(logout, { reason: SIGN_OUT_REASONS.SESSION_EXPIRED, next: currentPath() });
    },
    // Signed out (or expired) in another tab: follow it, rather than keep
    // offering actions this tab no longer has a session for.
    onSignedOutElsewhere: leaveAfterSignOutElsewhere
  });

  return (
    <AppShell
      sidebar={(
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed((value) => !value)}
        />
      )}
      header={<Header onMenuToggle={() => setIsSidebarOpen(true)} />}
      footer={<Footer />}
    >
      <AccountBanners />

      {/* Named the choice rather than the mechanism. "Your session is about to
          expire" describes the system's problem; this describes theirs, and
          the primary action is the one almost everybody wants. */}
      {warning ? (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="idle-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-background p-5 shadow-xl">
            <Text as="h2" id="idle-title" variant="title">Still there?</Text>
            <p className="mt-1.5 text-sm text-gray-600">
              You have been inactive for a while. To keep your work safe we will sign you out in{' '}
              <strong className="tabular-nums">{secondsLeft}</strong> second{secondsLeft === 1 ? '' : 's'}.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button size="sm"
                autoFocus
                onClick={staySignedIn}
                title="Keep me signed in">
                I’m Here
              </Button>
              <Button variant="outline" size="sm"
                onClick={() => signOutAndLeave(logout, { reason: SIGN_OUT_REASONS.SIGNED_OUT, to: '/' })}>
                Sign out
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Inner Suspense boundary: a lazy page suspends HERE, inside the shell,
          so the sidebar/header/footer persist and only the content area shows
          the loader — no full-screen chrome flash on navigation. */}
      <RefusalNotice />
      <Suspense fallback={<LoadingState label="Loading…" />}>
        <VerificationGate>{children}</VerificationGate>
      </Suspense>
    </AppShell>
  );
}
